'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import type { Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next/client';
import { useParams, usePathname } from 'next/navigation';
import { toast } from 'sonner';
import StoreLocation from './store-location';
import { getGoogleAddressFn } from '@/api/ahafood/get-address-detail-fn';
import { useAddAddress } from '@/api/use-add-new-address';
import { useIsMobile } from '@/lib/is-mobile';
import { CITIES, useAiFoodOrderStore } from '@/store';

export type StoreCityIdDialogProps = {
  showCityPopup: boolean;
  setShowCityPopup: (show: boolean) => void;
  address: Address | null;
  setAddress: (address: Address | null) => void;
  setNavigating?: (navigating: boolean) => void;
  session?: {
    store_name?: string;
    store_image_url?: string;
  } | null;
  clickOutside?: boolean;
};
export default function StoreCityIdSheet({
  showCityPopup,
  setShowCityPopup,
  address,
  setAddress,
  setNavigating,
  session,
  clickOutside = false,
}: StoreCityIdDialogProps) {
  const params = useParams();
  const pathname = usePathname();
  const setCityId = useAiFoodOrderStore.use.setCityId();
  const setSkipAutoLocation = useAiFoodOrderStore.use.setSkipAutoLocation();
  const isMobile = useIsMobile();
  const systemToken = getCookie('ws_token');
  const queryClient = useQueryClient();

  const generatePlaceId = (lat: number, lng: number): string => {
    return `${lat}_${lng}`;
  };

  // Function to reload page after setting address to ensure server gets updated data
  const reloadPageIfNeeded = () => {
    // Always reload after setting address to ensure server gets cookie data
    console.log('🔄 Reloading page to update server with new address data');

    // Small delay to ensure cookie is set
    setTimeout(() => {
      window.location.reload();
    }, 100);

    return true; // Always indicate that we're reloading
  };

  const addAddressMutation = useAddAddress(systemToken, {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['user-locations'],
      });
    },
  });

  const handleClose = () => {
    setShowCityPopup(false);
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleSetCity = (newAddress: Address): boolean => {
    if (newAddress?.second_label ?? newAddress?.address) {
      const matchedCity = CITIES.find((city) =>
        newAddress?.address?.includes(city.label)
      );
      if (matchedCity) {
        setCityId(matchedCity.value);

        const currentCitySlug = params.city as string;

        if (matchedCity.slug !== currentCitySlug) {
          // Mark as navigating to prevent other components from opening sheet
          setNavigating?.(true);

          if (pathname?.includes('/danh-sach-cua-hang')) {
            const localeSegment = params.locale as string;
            const splitToken = `/${currentCitySlug}`;
            const parts = pathname.split(splitToken);
            const rest = parts[1] ?? '';
            const newUrl = `/${localeSegment}/${matchedCity.slug}${rest}`;
            // Small delay to ensure cookie is set, then full reload
            setTimeout(() => {
              window.location.href = newUrl;
            }, 100);
            return true; // Indicate that we're handling redirect
          }
          // Check if we're on a store page (/cua-hang/[storeSlug])
          if (pathname?.includes('/cua-hang/')) {
            // Extract the store slug from current path
            const storeSlugMatch = pathname.match(/\/cua-hang\/([^/?]+)/);
            if (storeSlugMatch) {
              const storeSlug = storeSlugMatch[1];
              // Small delay to ensure cookie is set, then full reload
              setTimeout(() => {
                window.location.href = `/cua-hang/${storeSlug}`;
              }, 100);
              return true; // Indicate that we're handling redirect
            }
          }

          // For other pages, redirect to home with new city using full reload
          const newUrl = `/${matchedCity.slug}`;
          setTimeout(() => {
            window.location.href = newUrl;
          }, 100);
          return true; // Indicate that we're handling redirect
        }
      }
    }
    return false; // No city change, caller should handle page reload
  };

  const addAddressToServer = (addressToAdd: Address) => {
    if (
      addressToAdd?.address &&
      !!systemToken &&
      typeof addressToAdd.lat === 'number' &&
      typeof addressToAdd.lng === 'number' &&
      !addAddressMutation.isPending
    ) {
      // Tạo placeId từ lat và lng hoặc sử dụng place_id hiện có
      const placeId =
        addressToAdd.place_id ??
        generatePlaceId(addressToAdd.lat, addressToAdd.lng);

      addAddressMutation.mutate({
        address: addressToAdd.address,
        place_id: placeId,
        lat: addressToAdd.lat,
        lng: addressToAdd.lng,
      });
    }
  };

  const handleSetAddress = (newAddress: Address) => {
    if ((!newAddress.lat || !newAddress.lng) && newAddress.place_id) {
      const newPlace = {
        place_id: newAddress.place_id,
        sessiontoken: newAddress.sessiontoken,
      };
      getGoogleAddressFn(newPlace)
        .then((res: Address | null) => {
          if (res) {
            // Tạo placeId từ lat và lng nếu chưa có
            const addressWithPlaceId = {
              ...res,
              place_id: res.place_id ?? generatePlaceId(res.lat!, res.lng!),
            };

            // Handle city change first - returns true if redirecting
            const isRedirecting = handleSetCity(addressWithPlaceId);

            // Set address (this will automatically save to cookie via store)
            setAddress(addressWithPlaceId);
            addAddressToServer(addressWithPlaceId);
            // Reset skip auto location flag when address is set
            setSkipAutoLocation(false);

            // Only reload page if we're not redirecting to another city
            if (!isRedirecting) {
              // Reload page to get updated distance from server
              reloadPageIfNeeded();
            }
          }
        })
        .catch(() => {
          toast.error(
            'Đã có lỗi khi chọn địa chỉ của bạn, vui lòng thử lại sau'
          );
          setAddress(null);
        });

      if (!pathname?.includes('/cua-hang/')) {
        // Don't close popup, let the reload handle it
        // setShowCityPopup(false);
      }
      return;
    }

    // Handle city change first - returns true if redirecting
    const isRedirecting = handleSetCity(newAddress);

    // Set address (this will automatically save to cookie via store)
    setAddress(newAddress);
    addAddressToServer(newAddress);
    // Reset skip auto location flag when address is set
    setSkipAutoLocation(false);

    // Only reload page if we're not redirecting to another city
    if (!isRedirecting) {
      // Reload page to get updated distance from server
      reloadPageIfNeeded();
    }
  };

  const contentComponent = (
    <div className={cn('px-0', isMobile ? 'pt-0' : 'pt-2')}>
      <StoreLocation
        address={address}
        onAddressChange={handleSetAddress}
        onClose={handleClose}
        session={session}
        isMobile={isMobile}
      />
    </div>
  );

  if (!showCityPopup) return null;

  if (isMobile) {
    return (
      <Sheet
        open={showCityPopup}
        onOpenChange={(open) => {
          if (!open && clickOutside) {
            setShowCityPopup(false);
            setSkipAutoLocation(false);
          }
        }}
      >
        <SheetContent
          className={cn(
            'size-full max-w-lg mx-auto p-0 bg-neutral-10 gap-2 flex flex-col w-full',
            '[&>button]:data-[state=open]:hidden'
          )}
          side="bottom"
          onInteractOutside={(e) => {
            if (!clickOutside) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (!clickOutside) {
              e.preventDefault();
            }
          }}
        >
          <SheetTitle className="hidden"></SheetTitle>
          <div>
            {/* <ScrollArea className="[&>div>div]:gap-2 [&>div>div]:!flex [&>div>div]:flex-col [&>div>div]:w-full [&>div>div]:pb-28"> */}
            {address && (
              <Button
                size="icon"
                variant="secondary"
                className="[&_svg]:size-4 flex-none rounded-full absolute top-3 right-4 z-10"
                onClick={handleClose}
                type="button"
              >
                ✕
              </Button>
            )}
            {contentComponent}
          </div>
          {/* </ScrollArea> */}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog
      open={showCityPopup}
      onOpenChange={(open) => {
        if (!open && clickOutside) {
          setShowCityPopup(false);
          setSkipAutoLocation(false);
        }
      }}
    >
      <DialogTitle className="hidden"></DialogTitle>
      <DialogContent
        className="max-w-lg w-full h-[650px] p-0 gap-0 rounded-md overflow-hidden"
        onInteractOutside={(e) => {
          if (!clickOutside) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!clickOutside) {
            e.preventDefault();
          }
        }}
      >
        {address ? (
          <Button
            size="icon"
            variant="default"
            className="[&_svg]:size-6 flex-none rounded-full absolute top-3 right-3 z-10 bg-neutral-10 hover:bg-neutral-20 text-neutral-80"
            onClick={handleClose}
            type="button"
          >
            ✕
          </Button>
        ) : (
          <div className="absolute top-3 right-3 size-6 bg-white z-10"></div>
        )}

        {contentComponent}
      </DialogContent>
    </Dialog>
  );
}
